<template>
  <component :is="step.component" />
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, provide, ref } from 'vue';
import { useRoute } from 'vue-router/composables';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';

import {
  addProductToWallet,
  getLastState,
  removeProducts,
  resetPortfolio,
  createWallet,
  editWallet,
  IProductGetLastState
} from '@/modules/catalog/services/products-acquisition';

import {
  EProductsAcquisitionSteps,
  ProductsAcquitionComponents
} from './helpers';

interface Props {
  isBuilder: boolean;
  isEditingWallet: boolean;
  cameFrom: string;
}
const props = defineProps<Props>();

const route = useRoute();
const currentStep = ref(EProductsAcquisitionSteps.PRODUCTS);
const productLength = ref(0);
const productList = ref<IProductGetLastState[]>([]);
const isLoading = ref(false);
const closeModal = ref(false);
const portfolioCreatedId = ref('');
const portfolioCreatedName = ref('');

const step = computed(() => {
  return {
    component: ProductsAcquitionComponents[currentStep.value]
  };
});

async function fetchAddProductToWallet(productId: string) {
  try {
    isLoading.value = true;
    closeModal.value = false;

    const response = await addProductToWallet(productId);
    await fetchGetLastState(route.params.portfolioId as string);

    if (response.canAddInstrument) {
      notify({
        group: 'wrn-notifications',
        text: 'Produto adicionado',
        type: NotificationTypes.success
      });
    } else {
      notify({
        group: 'wrn-notifications',
        text: 'Produto indisponível',
        type: NotificationTypes.error
      });
    }

    closeModal.value = true;
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Houve um problema ao adicionar o produto. Tente novamente',
      type: NotificationTypes.error
    });
  } finally {
    isLoading.value = false;
  }
}

async function fetchRemoveProducts({ productIds }: { productIds: string[] }) {
  try {
    isLoading.value = true;
    closeModal.value = false;

    await removeProducts({ productIds });

    await fetchGetLastState(route.params.portfolioId as string);

    notify({
      group: 'wrn-notifications',
      text: 'Produto(s) removido(s)',
      type: NotificationTypes.success
    });

    closeModal.value = true;
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Houve um problema ao remover o(s) produto(s). Tente novamente',
      type: NotificationTypes.error
    });
  } finally {
    isLoading.value = false;
  }
}

async function fetchGetLastState(portfolioId?: string) {
  try {
    const isEditingWallet = Boolean(route.params.portfolioId);
    const { products } = await getLastState(portfolioId, isEditingWallet);

    productList.value = products;
    productLength.value = products.length ?? 0;
  } catch (e) {
    console.error(e);
  }
}

async function fetchCreateWallet(name: string) {
  try {
    const result = await createWallet(name);

    await resetPortfolio();

    portfolioCreatedId.value = result.id;
    portfolioCreatedName.value = result.name;

    currentStep.value = EProductsAcquisitionSteps.CONCLUSION;
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Houve um problema ao criar sua carteira. Tente novamente',
      type: NotificationTypes.error
    });
  }
}

async function confirmEditPortfolio(allocation: { portfolioId: string }) {
  try {
    const result = await editWallet(allocation.portfolioId);
    return result;
  } catch (error) {
    notify({
      group: 'wrn-notifications',
      text: 'Houve um problema ao confirmar a edição da carteira. Tente novamente',
      type: NotificationTypes.error
    });
    throw error;
  }
}

onMounted(async () => {
  await resetPortfolio();
  await fetchGetLastState(route.params.portfolioId as string);
});
onUnmounted(resetPortfolio);

provide('productList', productList);
provide('isLoading', isLoading);
provide('closeModal', closeModal);
provide('portfolioCreatedId', portfolioCreatedId);
provide('portfolioCreatedName', portfolioCreatedName);
provide('isBuilder', props.isBuilder);
provide('cameFrom', props.cameFrom);

provide('addProductToWallet', fetchAddProductToWallet);
provide('removeProductFromWallet', fetchRemoveProducts);
provide('createWallet', fetchCreateWallet);
provide('confirmEditPortfolio', confirmEditPortfolio);
provide('changeStep', (step: EProductsAcquisitionSteps) => {
  currentStep.value = step;
});
</script>
